import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Calendar,
  Col,
  Form,
  Row,
  Typography,
  Segmented,
  Table as AntTable,
  DatePicker,
  Space,
  Tag,
  Tooltip,
  Button,
} from 'antd';
import Select from 'components/Select';
import { getDistinctValues, toMoment } from 'utils/normalizers';
import styled from 'styled-components';
import { estadosCitasMap } from 'views/Tramites/GPM/components/ConsultaCita';
import moment from 'moment';
import { getNumOfSpaces } from 'views/Tramites/GPM/components/Cita';
import { useSelector } from 'react-redux';
import { docWidth } from 'utils/responsive';

const { Text } = Typography;

const COLORS = {
  white: '#FFFFFF',
  black: '#000000',
  gray: '#54636d',
  green: '#28a74580',
  yellow: '#fff20180',
  orange: '#ff950080',
  blue: '#365dec80',
  red: '#e31b1e80',
};

export const colorsByStatusMap = {
  [estadosCitasMap.PROGRAMADA]: COLORS.green,
  [estadosCitasMap.EN_PROCESO]: COLORS.orange,
  [estadosCitasMap.NO_ASISTIO]: COLORS.blue,
  [estadosCitasMap.FINALIZADA]: COLORS.yellow,
  [estadosCitasMap.CANCELADA]: COLORS.red,
};

const today = toMoment(new Date());

const months = Array(12).fill(0).map((_, i) => {
  const d = today.clone().set('months', i);
  return {
    value: d.month(),
    label: d.format('MMMM'),
  };
});

const years = Array(2).fill(0).map((_, i) => {
  const d = today.clone();
  d.year(d.year() + i);
  return {
    value: d.year(),
    label: d.year(),
  };
});

// eslint-disable-next-line
const CalendarSegmented = ({ setMode, mode, style, order }) => (
  <Col
    order={order}
    style={{ height: 32, marginBottom: order === 1 ? 10 : 0 }}
    span={order === 1 ? 24 : undefined}
  >
    <Space style={{ float: order === 1 ? 'right' : undefined, marginBottom: 5 }}>
      {order === 1 && (
        <Form.Item name="fecha" style={{ width: 150, height: 32, marginBottom: 0 }}>
          <DatePicker picker={mode === 'day' ? undefined : mode} allowClear={false} />
        </Form.Item>
      )}
      <Segmented
        style={{
          height: 32,
          fontWeight: 'bold',
          ...style,
        }}
        value={mode}
        onChange={setMode}
        options={[
          {
            label: 'Día',
            value: 'day',
          },
          {
            label: 'Semana',
            value: 'week',
          },
          {
            label: 'Mes',
            value: 'month',
          },
        ]}
      />
    </Space>
  </Col>
);

const headerRender = ({ onChange, value }, fetchCitas, mode, setMode) => (
  <Row gutter={10} justify="end" style={{ paddingTop: 10, paddingBottom: 10 }}>
    <Col style={{ height: 32 }} order={1}>
      <Select
        size="small"
        allowClear={false}
        showSearch={false}
        value={value?.format?.('YYYY')}
        onChange={(v) => {
          const d = value.clone().year(v);
          onChange(d);
          setTimeout(() => fetchCitas());
        }}
        dataSource={years}
        labelProp="label"
        valueProp="value"
        style={{ width: 180 }}
      />
    </Col>
    <Col style={{ height: 32 }} order={2}>
      <Select
        size="small"
        allowClear={false}
        showSearch={false}
        value={value?.month()}
        onChange={(v) => {
          const d = value.clone().month(v);
          onChange(d);
          setTimeout(() => fetchCitas());
        }}
        dataSource={months}
        labelProp="label"
        valueProp="value"
        style={{ width: 180 }}
      />
    </Col>
    <CalendarSegmented mode={mode} setMode={setMode} order={3} />
  </Row>
);

const dateFullCellRender = (date, fecha, citas) => {
  // ocultar fechas de otros meses
  if (date.get('month') !== fecha?.get('month')) return null;

  return (
    <Col span={24} style={{ margin: '0 3px', background: '#CCC' }}>
      <Text
        strong
        color="rgba(0, 0, 0, 0.5)"
        // style={{ lineHeight: `${60 - (_citas.length * 20)}px` }}
      >
        {date.get('date')}
      </Text>
      {/* {citas.filter((_, i) => i < 2).map((cita, i) => (
        <Text
          key={cita.id}
          className="xxs"
          style={{
            bottom: 0 + (i * 12) + 10,
            backgroundColor: colorsByStatusMap[cita.estado_de_cita],
          }}
        >
          {cita.folio}
        </Text>
      ))} */}
      {citas.length > 2 && (
        <Text
          style={{
            position: 'absolute',
            color: COLORS.white,
            top: 5,
            left: 65,
            width: 26,
            height: 26,
            fontSize: 12,
            lineHeight: '26px',
            borderRadius: '50%',
            backgroundColor: '#e31b1e',
            zIndex: 9,
          }}
        >
          {citas.length}
        </Text>
      )}
    </Col>
  );
};

const estadosColores = [
  [COLORS.green, 'Programada'],
  [COLORS.orange, 'En proceso'],
  [COLORS.blue, 'No se presentó'],
  [COLORS.yellow, 'Si se presentó'],
  [COLORS.red, 'Cancelada'],
];

const ColorsInfo = () => (
  <Col span={24} order={4} style={{ marginBottom: 15 }}>
    {estadosColores.map(([color, estado]) => (
      <Tag color={color} key={color}>
        <b style={{ color: '#000' }}>{estado}</b>
      </Tag>
    ))}
  </Col>
);

const AppointmentsCalendar = ({
  diasNoLaborales,
  citas: _citas,
  fetchCitas,
  oficina,
  onClickExpand,
}) => {
  const collapsedSidebar = useSelector(({ app }) => app.collapsedSidebar);
  const form = Form.useFormInstance();
  const mode = Form.useWatch('calendarMode', form);
  const fecha = Form.useWatch('fecha', form);

  const citas = useMemo(() => {
    const diffDates = getDistinctValues(_citas.map((c) => c.fecha_cita.format()));
    return diffDates.reduce((acum, curr) => ({
      ...acum,
      [curr]: _citas.filter((c) => c.fecha_cita.format() === curr),
    }), {});
  }, [_citas]);

  const setMode = (calendarMode, extra) => {
    form.setFieldsValue({ ...extra, calendarMode });
    if (mode === 'day' || (mode === 'week' && calendarMode === 'month')) setTimeout(() => fetchCitas());
  };

  const getColumns = () => {
    if (mode === 'month') {
      return [];
    }

    const onCell = ({ hora }, _d) => {
      const d = _d.hour(hora.hour()).minutes(hora.minutes());
      const cita = _citas.find((c) => d.isBetween(c.horario_inicio, c.horario_fin, undefined, '[)'));
      if (!cita) return { rowSpan: 1 };
      if (!cita.horario_inicio.isSame(d, 'minutes')) return { rowSpan: 0 };
      const diff = cita.horario_fin.diff(cita.horario_inicio, 'minutes');
      return { rowSpan: diff / 30 };
    };

    const dayWidth = docWidth - (collapsedSidebar ? 80 + 60 + 150 : 300 + 60 + 150);

    const cols = new Array(mode === 'day' ? 1 : 7).fill(0).map((col, i) => {
      const d = (fecha || today).clone();

      if (mode === 'week') {
        d.date(d.startOf('week').date()).add(i, 'day');
      }

      return {
        title: mode === 'day' ? d.format() : `${moment.weekdaysMin?.(i + 1)} ${d.date().toString().padStart(2, '0')}`,
        dataIndex: '_',
        // eslint-disable-next-line react/prop-types
        width: mode === 'day' && _citas.params?.ventanilla ? dayWidth : 200,
        render: (_, { hora }) => {
          d.hour(hora.hour()).minutes(hora.minutes());
          const citasHorario = _citas.filter((c) => c.horario_inicio.isSame(d, 'minutes'));
          if (!citasHorario.length) return '';
          const { rowSpan } = onCell({ hora }, d);
          const height = (rowSpan || 1) * 39;
          if (mode === 'week') {
            const citasPorEstado = estadosColores.map(([background, estado], idx) => ({
              background,
              estado,
              count: citasHorario.filter((c) => c.estado_de_cita === idx + 1).length,
            })).filter((e) => e.count);
            return (
              <Space>
                {citasPorEstado.map(({ count, background }) => (
                  <Button
                    type="link"
                    shape="circle"
                    style={{ background, fontWeight: 'bold' }}
                    onClick={() => setMode('day', { fecha: d })}
                    key={background}
                  >
                    {count}
                  </Button>
                ))}
              </Space>
            );
          }

          // eslint-disable-next-line react/prop-types
          // if (_citas.params?.ventanilla) {
          //   const [_cita] = citasHorario;
          //   return (
          //     <TagCita
          //       style={{
          //         height,
          //         backgroundColor: colorsByStatusMap[_cita.estado_de_cita],
          //         cursor: 'pointer',
          //       }}
          //       onClick={() => onClickExpand(_cita)}
          //     >
          //       <b style={{ lineHeight: `${height}px` }}>
          //         {_cita.folio}
          //         {' - '}
          //         {_cita.nombre_solicitante}
          //         {' - '}
          //         {_cita.plantilla_de_tramite__nombre}
          //       </b>
          //     </TagCita>
          //   );
          // }

          return (
            <div
              style={{
                // width: 700,
                display: 'flex',
                flexDirection: 'row',
                width: dayWidth,
                // flexFlow: 'row', // TODO: revisar porque rompe tabla
                overflow: 'scroll hidden',
              }}
            >
              {citasHorario.map((_cita) => (
                <div key={_cita.id} style={{ marginInline: 5 }}>
                  <Tooltip
                    title={(
                      <>
                        {_cita.nombre_solicitante}
                        <br />
                        {_cita.plantilla_de_tramite__nombre}
                      </>
                    )}
                  >
                    <TagCita
                      style={{
                        backgroundColor: colorsByStatusMap[_cita.estado_de_cita],
                        height,
                        width: 'max-content',
                        padding: '0 5px',
                        cursor: 'pointer',
                      }}
                      onClick={() => onClickExpand(_cita)}
                      $hoverable
                    >
                      <b style={{ lineHeight: `${height}px` }}>
                        {_cita.folio}
                      </b>
                    </TagCita>
                  </Tooltip>
                </div>
              ))}
            </div>
          );
        },
        onCell: (record) => onCell(record, d),
      };
    });

    return [
      {
        title: 'Hora',
        dataIndex: 'hora',
        width: 100,
        render: (val) => <b>{val.format('hh:mm a')}</b>,
      },
      ...cols,
    ];
  };

  const columns = getColumns();

  const makeFakeRows = () => {
    if (!oficina) return [];
    const configAtencion = { horario_unidad_de_plazo: 2, horario_cantidad: 30 };
    const rows = oficina.horarios
      .map((h, _i) => {
        const { numOfSpaces, entrada } = getNumOfSpaces(configAtencion, h, fecha);
        return new Array(numOfSpaces).fill(null)
          .map((_, i) => ({ hora: toMoment(entrada.clone().add(30 * i, 'm')), key: `${_i}${i}` }));
      }).flat();
    return rows;
  };

  if (mode !== 'month') {
    const dataSource = makeFakeRows();
    return (
      <>
        <CalendarSegmented
          order={1}
          mode={mode}
          setMode={setMode}
        />
        <Col span={24} order={5} key={`${fecha?.format()}-${mode}-${oficina?.id}`}>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="key"
            pagination={{ pageSize: dataSource.length, hideOnSinglePage: true }}
            scroll={{ y: mode === 'day' ? undefined : 800, x: columns.reduce((acum, curr) => acum + curr.width, 0) }}
          />
        </Col>
        <ColorsInfo />
      </>
    );
  }

  return (
    <>
      <Col flex="770px">
        <Form.Item name="fecha">
          <StyledCalendar
            mode="month"
            fullscreen={false}
            value={fecha}
            dateFullCellRender={(d) => dateFullCellRender(
              d,
              fecha,
              citas[d.format()] || [],
              diasNoLaborales,
            )}
            headerRender={(props) => headerRender(props, fetchCitas, mode, setMode)}
            disabledDate={(d) => d.get('month') !== fecha?.get('month')}
          />
        </Form.Item>
      </Col>
      <ColorsInfo />
    </>
  );
};

const Table = styled(AntTable)`
  th, td {
    padding: 8px !important;
  }
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0 !important;
  }
  /* .ant-table-tbody .ant-table-measure-row {
    display: none;
  } */
`;

export const TagCita = styled.div`
  height: 100%;
  padding: 2px;
  border-radius: 5px;
  text-align: center;
  font-weight: bold;
  b {
    color: ${COLORS.black};
    padding-left: 5px;
  }
  ${(props) => props.$hoverable && `
    &:hover {
      border: 1px solid #ccc;
    }
  `}
`;

const StyledCalendar = styled(Calendar)`
  font-size: 16px;
  width: 100%;
  .ant-picker-cell.ant-picker-cell-in-view {
    padding: 0;
  }
  .ant-col {
    height: 80px;
    margin-inline: 5px;
  }
  table {
    border-spacing: 0;
  }
  .ant-picker-cell {
    .ant-typography {
      width: 80px;
      line-height: 80px;
      font-size: 14px;
    }
    .xxs {
      text-align: start;
      width: calc(100% - 20px);
      font-size: 7px !important;
      line-height: 7px;
      margin-bottom: 2px;
      position: absolute;
      left: 10px;
      padding: 2px;
    }
    &:before {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      margin-left: calc((100% - 60px) / 2);
    }
  }
  .ant-picker-cell-selected:before {
    border: 1px solid ${COLORS.gray};
  }
  .ant-picker-cell-disabled:before {
    background: none !important;
  }
`;

AppointmentsCalendar.propTypes = {
  citas: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  diasNoLaborales: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchCitas: PropTypes.func.isRequired,
  oficina: PropTypes.shape(),
  onClickExpand: PropTypes.func.isRequired,
};

AppointmentsCalendar.defaultProps = {
  oficina: null,
};

export default AppointmentsCalendar;
